import moment from "moment";
import Tooltip from "rc-tooltip";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Icon, Input, Modal, Progress, Segment } from "semantic-ui-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import type {
	CreateDataLayerUploadRequest,
	DataLayerTypeSelectOption,
	DataLayerUpload,
	UUID,
} from "../../../../apis/types/dataLayerTypes";
import {
	createRasterDataLayerUploadWithProgress,
	fetchFromAzureFileShare,
	rasterDataLayerUploadsQueryOptions,
} from "../../../../apis/dataLayerApi";
import { AerialPlotResponse } from "../../../../apis/types";
import SimpleOkCancelModal from "../../../Lumber/SimpleOkCancelModal";
import { useAuth0 } from "../../../../auth/auth0";

import styles from "./styles.module.css";

interface RasterUploadRowProps {
	clientId: UUID;
	dataLayerId: UUID;
	dataLayerTypeSelectOptions: DataLayerTypeSelectOption[];
	upload: DataLayerUpload;
	isDeleting: boolean;
	isDisabled: boolean;
	onBeforeModify: (action: () => void) => void;
	onDelete: (upload: DataLayerUpload) => void | Promise<void>;
	onUploadResponse: (pendingId: UUID, newId: UUID, response: AerialPlotResponse<DataLayerUpload>) => void;
}

const DATE_FORMAT = "MM/DD/YYYY";

/**
 * Raster Data Layer Upload Form Control: Upload/Download/Delete management for one raster
 * DataLayerUpload record. Simpler than VectorUploadRow — no configurator modal.
 */
const RasterUploadRow = ({
	clientId,
	dataLayerId,
	dataLayerTypeSelectOptions,
	upload,
	isDeleting,
	isDisabled,
	onBeforeModify,
	onDelete,
	onUploadResponse,
}: RasterUploadRowProps) => {
	const { getTokenSilently } = useAuth0() as { getTokenSilently: () => Promise<string> };

	/*
	 * State
	 */
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);
	const [selectedDataLayerType, setSelectedDataLayerType] = useState<UUID | null>(null);
	const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
	const [uploadProgress, setUploadProgress] = useState<number | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);

	/*
	 * Queries and Mutations
	 */
	const queryClient = useQueryClient();
	const { mutateAsync: rasterDataLayerUploadMutationAsync } = useMutation({
		mutationFn: createRasterDataLayerUploadWithProgress,
		onSuccess: () => {
			const { queryKey } = rasterDataLayerUploadsQueryOptions(getTokenSilently, clientId, dataLayerId);
			void queryClient.invalidateQueries({ queryKey });
		},
		onSettled: () => {
			setUploadProgress(null);
			setIsUploading(false);
			setIsProcessing(false);
			abortControllerRef.current = null;
		},
	});

	/*
	 * Effects
	 */

	// Extract display name from the rawDataUrlPath (Azure path format: {4-part-uuid}-filename)
	useEffect(() => {
		if (upload.rawDataUrlPath) {
			const pathParts = upload.rawDataUrlPath.split("-");
			const fileName = pathParts.slice(5).join("-");
			setSelectedFileName(fileName);
		}
	}, [upload]);

	/*
	 * Memoization
	 */

	const isUploaded = useMemo(() => {
		return !isUploading && !!upload.rawDataUrlPath;
	}, [isUploading, upload]);

	const confirmDeleteMsg = useMemo(
		() => `Raster data upload will be deleted permanently: ${selectedFileName}`,
		[selectedFileName],
	);

	const progressModalHeader = useMemo(() => (isProcessing ? "Saving" : "Uploading"), [isProcessing]);

	const progressModalMsg = useMemo(
		() =>
			isProcessing
				? `Please wait, saving ${selectedFile?.name}...`
				: `Please wait, uploading ${selectedFile?.name} (${selectedFile?.size} bytes, ${uploadProgress?.toFixed(0)}%)`,
		[isProcessing, selectedFile, uploadProgress],
	);

	const dateUploadedFmt = useMemo(() => {
		const m = moment.utc(upload.createdDateTimeUtc);
		return m.isValid() ? m.local().format(DATE_FORMAT) : moment.utc().local().format(DATE_FORMAT);
	}, [upload.createdDateTimeUtc]);

	/*
	 * Callbacks
	 */

	const handleFileBrowse = useCallback(() => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	}, []);

	const handleFileSelected = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			const fileData = event.target.files[0];
			setSelectedFileName(fileData.name);
			setSelectedFile(fileData);
		}
	}, []);

	const handleRemove = useCallback(async () => {
		await onDelete(upload);
	}, [upload, onDelete]);

	const handleDeleteModalCancel = useCallback(() => {
		setDeleteModalIsOpen(false);
	}, []);

	const handleDeleteModalOk = useCallback(async () => {
		setDeleteModalIsOpen(false);
		await onDelete(upload);
	}, [upload, onDelete]);

	const handleDeleteModalRequest = useCallback(() => {
		setDeleteModalIsOpen(true);
	}, []);

	const handleDataLayerTypeChange = useCallback(
		(
			_event: React.SyntheticEvent<HTMLElement>,
			data: { value?: string | number | boolean | (string | number | boolean)[] },
		) => {
			setSelectedDataLayerType((data.value as UUID) ?? null);
		},
		[],
	);

	const handleUploadProgress = useCallback((event: ProgressEvent) => {
		if (event.lengthComputable && event.total > 0) {
			const percent = (event.loaded / event.total) * 100;
			setUploadProgress(percent);
			if (percent >= 100) {
				setIsProcessing(true);
			}
		}
	}, []);

	const handleUploadRequest = useCallback(
		async (event: React.MouseEvent) => {
			event.preventDefault();
			if (!selectedDataLayerType || !selectedFile) {
				throw Error("Invalid upload request: type and file are required");
			}
			setIsUploading(true);
			abortControllerRef.current = new AbortController();
			const request: CreateDataLayerUploadRequest = {
				dataLayerId,
				dataLayerTypeId: selectedDataLayerType,
				file: selectedFile,
			};
			try {
				const uploadResponse = await rasterDataLayerUploadMutationAsync({
					getAccessToken: getTokenSilently,
					clientId,
					request,
					onProgress: handleUploadProgress,
					signal: abortControllerRef.current.signal,
				});
				const newId = uploadResponse.data.id;
				if (!newId) {
					throw Error("Invalid response data (no uuid)");
				}
				onUploadResponse(upload.id, newId, uploadResponse);
			} catch (error) {
				if (error instanceof Error) {
					if (error.name === "AbortError" || error.message === "canceled") {
						toast.info("Upload cancelled by user.");
						return;
					}
					console.error(error);
					toast.error(error.message);
				} else {
					console.error(error);
					toast.error("Upload failed");
				}
			}
		},
		[
			selectedFile,
			selectedDataLayerType,
			dataLayerId,
			getTokenSilently,
			clientId,
			rasterDataLayerUploadMutationAsync,
			handleUploadProgress,
			onUploadResponse,
			upload.id,
		],
	);

	const handleCancelUpload = useCallback(() => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
		setIsUploading(false);
	}, []);

	const handleDownload = useCallback(
		async (evt: React.MouseEvent) => {
			evt.preventDefault();
			if (!upload.rawDataSasUrl) return;
			toast.info("Download started, please wait...");
			const response = await fetchFromAzureFileShare(upload.rawDataSasUrl);
			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = selectedFileName || "raster-download";
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		},
		[upload.rawDataSasUrl, selectedFileName],
	);

	/*
	 * Rendering
	 */

	return (
		<>
			<Form className={styles.rasterDataLayerCtrl}>
				<Form.Group className={styles.uploadRowGrid}>
					<Form.Field className={`${styles.uploadCell} ${styles.uploadCellAction}`}>
						{!isUploaded && (
							<Tooltip placement="top" overlay="Remove new upload record">
								<span className={styles.tooltipContent}>
									<Button
										icon="minus"
										disabled={isDeleting || isDisabled}
										onClick={() => void handleRemove()}
										size="small"
									/>
								</span>
							</Tooltip>
						)}
					</Form.Field>

					<Form.Field className={`${styles.uploadCell} ${styles.uploadCellDownload}`}>
						<Tooltip placement="top" overlay="Download source raster file">
							<span className={styles.tooltipContent}>
								<Button
									onClick={(evt: React.MouseEvent) => void handleDownload(evt)}
									icon="download"
									disabled={isDisabled || !upload.rawDataSasUrl || isDeleting}
									size="small"
								/>
							</span>
						</Tooltip>
					</Form.Field>

					<Form.Field className={`${styles.uploadCell} ${styles.uploadCellType}`}>
						{isUploaded ? (
							<div className={`${styles.centerText} ${styles.uploadTypeReadonly}`}>
								{dataLayerTypeSelectOptions.find((dlt) => dlt.value === upload.dataLayerTypeId)?.text}
							</div>
						) : (
							<Form.Select
								id="form-select-raster-data-layer-type"
								placeholder="Select type of raster"
								options={dataLayerTypeSelectOptions}
								disabled={isDisabled || isUploading}
								value={selectedDataLayerType || upload.dataLayerTypeId}
								multiple={false}
								onChange={handleDataLayerTypeChange}
							/>
						)}
					</Form.Field>

					<Form.Field className={`${styles.uploadCell} ${styles.uploadCellBrowse}`}>
						{!isUploaded && (
							<Tooltip placement="top" overlay="Find source raster file on local filesystem">
								<span className={styles.tooltipContent}>
									<Button
										content="Browse local..."
										labelPosition="left"
										icon="folder open solid"
										disabled={isDisabled || isUploading || isDeleting}
										onClick={handleFileBrowse}
										size="small"
									/>
								</span>
							</Tooltip>
						)}
					</Form.Field>

					<input
						ref={fileInputRef}
						type="file"
						accept=".tif,.tiff,.img,.jp2,.png,.jpg,.jpeg"
						hidden
						onChange={handleFileSelected}
					/>

					<Form.Field className={`${styles.uploadCell} ${styles.uploadCellDelete}`}>
						<Tooltip placement="top" overlay="Delete raster data layer upload">
							<span className={styles.tooltipContent}>
								<Button
									onClick={handleDeleteModalRequest}
									icon={isDeleting ? "spinner" : "trash"}
									disabled={isDisabled || !isUploaded || isDeleting}
								/>
							</span>
						</Tooltip>
					</Form.Field>

					<Form.Field className={`${styles.uploadCell} ${styles.uploadCellFile}`}>
						<Input disabled value={selectedFileName || ""} />
					</Form.Field>

					<Form.Field className={`${styles.uploadCell} ${styles.uploadCellFinal}`}>
						{isUploaded && (
							<div className={`${styles.centerText} ${styles.rasterDateLabel}`}>Date Uploaded: {dateUploadedFmt}</div>
						)}
						{!isUploaded && (
							<Button
								primary={!!selectedFileName && !!selectedDataLayerType}
								type="submit"
								disabled={!selectedFileName || !selectedDataLayerType || isUploading || isDeleting}
								onClick={(evt: React.MouseEvent) => onBeforeModify(() => void handleUploadRequest(evt))}
								compact
							>
								<Icon name="cloud upload" />
								Upload
							</Button>
						)}
					</Form.Field>
				</Form.Group>
			</Form>

			<Modal open={isUploading} closeOnDimmerClick={false} closeOnEscape={false}>
				<Modal.Header>{progressModalHeader}</Modal.Header>
				<Modal.Content>
					<Modal.Description>
						<Segment basic>{progressModalMsg}</Segment>
						{uploadProgress !== null && <Progress active percent={uploadProgress} />}
					</Modal.Description>
				</Modal.Content>
				<Modal.Actions>
					<Button onClick={handleCancelUpload}>Cancel</Button>
				</Modal.Actions>
			</Modal>

			<SimpleOkCancelModal
				headerMsg="Confirm Delete"
				bodyMsg={confirmDeleteMsg}
				open={deleteModalIsOpen}
				onOk={() => void handleDeleteModalOk()}
				onCancel={handleDeleteModalCancel}
			/>
		</>
	);
};

export default RasterUploadRow;
